import { CheckOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';

import { TrendArrowDown } from 'assets/svg/TrendArrowDown';
import { TrendArrowUp } from 'assets/svg/TrendArrowUp';
import { colorConstants } from 'config/colorConstants';

type ScopeIconKind = 'all' | 'partial';
type DirectionIconKind = 'increase' | 'decrease';

type RecommendationLevelRow = {
    label: string;
    left: string;
    right: string;
};

type LegendRow = {
    icon: ScopeIconKind;
    label: string;
};

type DirectionRow = {
    icon: DirectionIconKind;
    label: string;
};

const LABEL_COLUMN_WIDTH = 80;
/**
 * Needs to be config driven and should be passed as props to the component
*/
const recommendationLevelContent = {
    suggestionHeader: 'AI Suggestion',
    levelHeader: 'Recommendation Level',
    separator: '×',
    rows: [
        { label: 'Delist', left: 'SKU', right: 'Retailer' },
        { label: 'Distribution', left: 'SKU', right: 'Retailer' },
        { label: 'Price', left: 'PPG', right: 'Retailer' },
        { label: 'Pack', left: 'PPG', right: 'Channel' },
    ] as RecommendationLevelRow[],
};

const legendContent = {
    title: 'Legend',
    rows: [
        { icon: 'all', label: 'Applies to all Products' },
        { icon: 'partial', label: 'Applies to few Products' },
    ] as LegendRow[],
};

const directionContent = {
    title: 'Direction',
    rows: [
        { icon: 'increase', label: 'Suggestion for Increase' },
        { icon: 'decrease', label: 'Suggestion for Decrease' },
    ] as DirectionRow[],
};

const exampleContent = {
    title: 'Example',
    label: 'Price',
    scopeIcon: 'partial' as ScopeIconKind,
    directions: ['decrease', 'increase'] as DirectionIconKind[],
    description: '“AI recommends price increase for few and decrease for few products”',
};

const StyledTooltipCard = styled.div`
    width: 280px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px;
    border-radius: 12px;
    background: ${colorConstants.white};
    color: ${colorConstants.blackTitle};
    font-family: 'IBM Plex Sans', sans-serif;
    box-shadow: 0 6px 8px ${colorConstants.blackTransparent08},
        0 3px 3px ${colorConstants.blackTransparent12}, 0 9px 14px ${colorConstants.blackTransparent05};
`;

const StyledSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
`;

const StyledSectionTitle = styled.p`
    margin: 0;
    font-size: 10px;
    font-weight: 400;
    line-height: 14px;
    color: ${colorConstants.toolbarLabelGrey};
`;

const StyledHeaderRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    font-size: 10px;
    font-weight: 400;
    line-height: 18px;
    color: ${colorConstants.toolbarLabelGrey};
`;

const StyledRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
`;

const StyledLabelColumn = styled.div`
    display: flex;
    align-items: center;
    width: ${LABEL_COLUMN_WIDTH}px;
    flex-shrink: 0;
    gap: 4px;
`;

const StyledSuggestionLabel = styled.span`
    font-size: 12px;
    font-weight: 500;
    line-height: 22px;
    color: ${colorConstants.sliderTooltipBg};
    white-space: nowrap;
`;

const StyledRowText = styled.p`
    margin: 0;
    flex: 1;
    min-width: 0;
    font-size: 12px;
    font-weight: 400;
    line-height: 18px;
    color: ${colorConstants.blackTitle};
`;

const StyledDivider = styled.div`
    width: 100%;
    height: 1px;
    background: ${colorConstants.tooltipDividerGrey};
`;

const StyledScopeIcon = styled.span`
    position: relative;
    display: inline-flex;
    width: 12px;
    height: 12px;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    background: ${colorConstants.breadcrumbSlash};
`;

const StyledPartialMark = styled.span`
    width: 6.9px;
    height: 1.3px;
    border-radius: 1px;
    background: ${colorConstants.white};
`;

const StyledDirectionIcon = styled.span<{ $size: number }>`
    display: inline-flex;
    width: ${({ $size }) => $size}px;
    height: ${({ $size }) => $size}px;
    flex-shrink: 0;
    overflow: hidden;

    svg {
        width: 100%;
        height: 100%;
    }
`;

const StyledExampleTrigger = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
`;

const StyledExampleDirections = styled.span`
    display: inline-flex;
    align-items: center;

    > *:first-child {
        margin-right: -4px;
    }
`;

const ScopeIcon = ({ kind }: { kind: ScopeIconKind }) => (
    <StyledScopeIcon>
        {kind === 'all' ? (
            <CheckOutlined style={{ fontSize: 7, color: colorConstants.white }} />
        ) : (
            <StyledPartialMark />
        )}
    </StyledScopeIcon>
);

const DirectionIcon = ({ kind, size = 16 }: { kind: DirectionIconKind; size?: number }) => (
    <StyledDirectionIcon $size={size}>
        {kind === 'increase' ? (
            <TrendArrowUp color={colorConstants.growthUp} />
        ) : (
            <TrendArrowDown color={colorConstants.growthDown} />
        )}
    </StyledDirectionIcon>
);

const AiColumnHeaderTooltip = () => (
    <StyledTooltipCard>
        <StyledSection>
            <StyledHeaderRow>
                <span style={{ width: LABEL_COLUMN_WIDTH, flexShrink: 0 }}>
                    {recommendationLevelContent.suggestionHeader}
                </span>
                <span style={{ flex: 1 }}>{recommendationLevelContent.levelHeader}</span>
            </StyledHeaderRow>
            {recommendationLevelContent.rows.map((row) => (
                <StyledRow key={row.label}>
                    <StyledLabelColumn>
                        <StyledSuggestionLabel>{row.label}</StyledSuggestionLabel>
                    </StyledLabelColumn>
                    <StyledRowText>
                        {row.left}{' '}
                        <span style={{ color: colorConstants.toolbarLabelGrey }}>
                            {recommendationLevelContent.separator}
                        </span>{' '}
                        {row.right}
                    </StyledRowText>
                </StyledRow>
            ))}
        </StyledSection>

        <StyledDivider />

        <StyledSection>
            <StyledSectionTitle>{legendContent.title}</StyledSectionTitle>
            {legendContent.rows.map((row) => (
                <StyledRow key={row.label}>
                    <StyledLabelColumn>
                        <ScopeIcon kind={row.icon} />
                    </StyledLabelColumn>
                    <StyledRowText>{row.label}</StyledRowText>
                </StyledRow>
            ))}
        </StyledSection>

        <StyledDivider />

        <StyledSection>
            <StyledSectionTitle>{directionContent.title}</StyledSectionTitle>
            {directionContent.rows.map((row) => (
                <StyledRow key={row.label}>
                    <StyledLabelColumn>
                        <DirectionIcon kind={row.icon} />
                    </StyledLabelColumn>
                    <StyledRowText>{row.label}</StyledRowText>
                </StyledRow>
            ))}
        </StyledSection>

        <StyledDivider />

        <StyledSection>
            <StyledSectionTitle>{exampleContent.title}</StyledSectionTitle>
            <StyledRow>
                <StyledExampleTrigger>
                    <ScopeIcon kind={exampleContent.scopeIcon} />
                    <StyledSuggestionLabel>{exampleContent.label}</StyledSuggestionLabel>
                    <StyledExampleDirections>
                        {exampleContent.directions.map((direction) => (
                            <DirectionIcon key={direction} kind={direction} size={12} />
                        ))}
                    </StyledExampleDirections>
                </StyledExampleTrigger>
                <StyledRowText>{exampleContent.description}</StyledRowText>
            </StyledRow>
        </StyledSection>
    </StyledTooltipCard>
);

export default AiColumnHeaderTooltip;
